import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { CheckCircleFill, FileEarmark, PlusCircle } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS, TASK_ROLES, servicesKeyValue } from '../../../helpers/constants';
import { getUserFromLocalstrorage, isClient } from '../../../helpers/global';
import { onScrollToErrorPosition } from '../../../helpers/ui';
import useAppChoices from '../../../hooks/useAppChoices';
import AlertModal from '../../AlertModal';
import ClientInfo from '../../ClientInfo';
import SlidingSideBar from '../../SlidingSideBar/SlidingSideBar';
import AddEditOrderSidebar from './AddEditOrderSidebar';
import ServiceForm from './Form/ServiceForm';
import Estoppel from './estoppel';
import LienSearch from './lien-search';
import Survey from './survey';
import { NewOrder } from '../../newOrder';

const ORDER_FOR_AN_SERVICE = [
  {
    label: 'Lien Search',
    key: 'lienSearch'
  },
  {
    label: 'Estoppel',
    key: 'estoppel'
  },
  {
    label: 'Survey',
    key: 'survey'
  }
];

const createObjectWithoutEmptyValues = inputObj => {
  const newObj = {};

  for (const key in inputObj) {
    if (
      inputObj.hasOwnProperty(key) &&
      ((inputObj[key] !== null && inputObj[key] !== undefined) || inputObj[key]?.length > 0)
    ) {
      newObj[key] = inputObj[key];
    }
  }

  return newObj;
};

const AddEditServiceSidebar = ({ show, editingService, onHide, onServiceAddUpdate, isPublic, onPublicOrderSubmit }) => {
  const orgs = useAppChoices('organizations');
  const [loading, setLoading] = useState(false);
  const [service, setService] = useState({});
  const [addUpdateOrderMeta, setAddUpdateOrderMeta] = useState(null);
  const [deleteOrderMeta, setDeleteOrderMeta] = useState(null);
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const user = useMemo(() => getUserFromLocalstrorage(), []);

  const [selectedOrganization, setSelectedORganization] = useState(
    isPublic ? undefined : isClient(role) ? orgs.find(o => o._id === user.organization?._id) : orgs[0]
  );

  // have to remove createdby should be set in backend
  const isAdd = editingService?._id ? false : true;

  const placedOrderKeys = useMemo(
    () => service?.orders?.map(o => ORDER_FOR_AN_SERVICE.find(os => os.label === o.orderType).key) || [],
    [service]
  );

  const processors = useAppChoices('users').filter(u => TASK_ROLES.includes(u.role));

  const onSubmitServiceForm = async value => {
    const formData = createObjectWithoutEmptyValues({ ...value });
    if (isAdd) {
      if (formData.buyerEmail === '') {
        delete formData.buyerEmail;
      }
      if (formData.sellerEmail === '') {
        delete formData.sellerEmail;
      }
      if (!service?.orders || service?.orders?.length <= 0) {
        toast.error('Order needs to be placed');
        onScrollToErrorPosition('orders');
        return;
      }
      formData['orders'] = service?.orders.map(o => {
        const newObj = { ...o };
        delete newObj._id;
        return newObj;
      });
    }
    if (isPublic) {
      onPublicOrderSubmit(formData);
      return;
    }
    setLoading(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: !isAdd ? ENDPOINTS.SERVICE_WITH_ID(editingService._id) : ENDPOINTS.CREATE_SERVICE,
        method: !isAdd ? 'PUT' : 'POST',
        requestBody: formData
      });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success(!isAdd ? 'Successfully Updated' : 'Service successfully Created');
      onServiceAddUpdate(
        !isAdd && !response?.municipality && editingService?.municipality
          ? { ...response, municipality: editingService.municipality }
          : response
      );
      onHide();
      setService({});
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onUpdateFormOrder = data => {
    const index = (service?.orders ? service?.orders : []).findIndex(o => o.orderType === data.orderType);
    if (index != -1) {
      service['orders'][index] = data;
    } else {
      service['orders'] = [...(service?.['orders'] || []), data];
    }
    setService({ ...service });
    setAddUpdateOrderMeta(null);

    onScrollToErrorPosition(ORDER_FOR_AN_SERVICE.find(o => o.label === data.orderType).key);
  };

  const onOrderEditClick = order => {
    setAddUpdateOrderMeta({ title: order.orderType, object: order, hoaEditIndex: null });
  };

  const onEditHoaSlotClick = (order, hoaIndex) => {
    setAddUpdateOrderMeta({ title: order.orderType, object: order, hoaEditIndex: hoaIndex });
  };

  const onOrderDeleteClick = order => {
    setDeleteOrderMeta({ toBeDeletedOrder: order });
  };

  const deleteOrder = async () => {
    const { toBeDeletedOrder } = deleteOrderMeta;
    setDeleteOrderMeta({ ...deleteOrderMeta, deletingOrder: true });

    setService({ ...service, orders: service.orders.filter(s => s._id !== toBeDeletedOrder._id) });
    setDeleteOrderMeta(null);
    toast.success('Order deleted successfully');
  };

  const handleOrderForService = (serviceOrder, label) => {
    if (placedOrderKeys.includes(serviceOrder)) return;

    setAddUpdateOrderMeta({ title: label });
  };

  const onClose = () => {
    if (!isAdd) {
      onHide();
      return;
    }
    service?._id && onServiceAddUpdate(service);
    setService({});
    onHide();
  };

  if (isAdd && show) {
    return (
      <SlidingSideBar modal fullScreen={false} visible={show} onClose={onClose} showCloseButton={true}>
        <NewOrder
          onClose={onClose}
          isInternal
          onServiceCreate={service => {
            onServiceAddUpdate(service);
          }}
        />
      </SlidingSideBar>
    );
  }

  return (
    <SlidingSideBar
      fullScreen
      visible={show}
      onClose={onClose}
      showCloseButton={!isPublic}
      title={!isAdd ? 'Update services' : isAdd && !service?._id ? 'Add services' : 'Add Orders'}
    >
      <AlertModal
        show={deleteOrderMeta !== null}
        onHide={() => setDeleteOrderMeta(null)}
        onDismissClick={() => setDeleteOrderMeta(null)}
        onContinueClick={deleteOrder}
        showProgress={deleteOrderMeta?.deletingOrder}
        alertText={`Are you sure you want to delete this order ? This action cannot be undone!`}
        progressText="Deleting order..."
      />
      {isAdd && (
        <div className="ml-4">
          <p className="mb-0 largeFont font-weight-bold">
            {`Step 1 of 2: `}
            <b>{'Basic Details'}</b>
          </p>

          <p className="midFont font-weight-bold text-muted">Please fill in the basic details of your request.</p>

          <span
            className="bg-grey mt-1 mb-4"
            style={{
              height: '1px',
              display: 'block'
            }}
          />
        </div>
      )}
      {isClient(role) && !service?._id && <ClientInfo />}
      {show && (
        <ServiceForm
          onClose={onClose}
          onChangeSelectedOrganization={o => setSelectedORganization(o)}
          loading={loading}
          onSubmit={onSubmitServiceForm}
          isEditingService={editingService}
        />
      )}

      {isAdd && (
        <>
          <hr />
          <div className="ml-3">
            <p className="mb-0 largeFont font-weight-bold">
              {`Step 2 of 2: `}
              <b>{'Place Orders'}</b>
            </p>

            <p className="midFont font-weight-bold text-muted">
              Select the Order Type you wish to place with this service. Complete the form that appears, and then tap
              'Add' to include the order with this service.
            </p>
          </div>
          <hr />

          {/* Order For Service Selection */}
          <div id="orders" className="d-flex justify-content-start align-items-center  mb-3">
            {ORDER_FOR_AN_SERVICE.map(({ label, key }) => {
              const serviceIsOrdered = placedOrderKeys.includes(key);
              return (
                <Button
                  key={key}
                  onClick={() => handleOrderForService(key, label)}
                  className={`mx-2 border border-dark-grey`}
                  variant={serviceIsOrdered ? 'dark' : 'white'}
                >
                  {!serviceIsOrdered ? `Add ${label}` : label}
                  {serviceIsOrdered ? (
                    <CheckCircleFill className="ml-2 text-success" />
                  ) : (
                    <PlusCircle className="ml-2 text-danger" />
                  )}
                </Button>
              );
            })}
          </div>

          <hr />

          {/* Add Estoples, Lien Search and Survey */}

          {servicesKeyValue.slice(1, servicesKeyValue.length).map(a => (
            <React.Fragment key={a.key}>
              {service?.orders?.find(v => v.orderType === a.name) && (
                <div id={a.key} className="mx-2">
                  <div className="d-flex">
                    <FileEarmark fontSize={20} />
                    <p className="largeFont font-weight-bold ml-1">{a.name}</p>
                  </div>
                  <span
                    className="d-block bg-grey mt-1 mb-3"
                    style={{
                      height: '1px',
                      display: 'block'
                    }}
                  />
                  <Card className="p-2 mb-4">
                    {a.key === 'lienSearch' && service?.orders?.find(v => v.orderType === 'Lien Search') && (
                      <LienSearch
                        serviceCreation={true}
                        lienSearch={{
                          ...service?.orders?.find(v => v.orderType === 'Lien Search'),
                          assignedProcessors:
                            processors.filter(p =>
                              service?.orders
                                ?.find(v => v.orderType === 'Lien Search')
                                ?.assignedProcessors?.includes(p._id)
                            ) || []
                        }}
                        onEditClick={onOrderEditClick}
                        onDeleteClick={onOrderDeleteClick}
                      />
                    )}
                    {a.key === 'estoppel' && service?.orders?.find(v => v.orderType === 'Estoppel') && (
                      <Estoppel
                        serviceCreation={true}
                        estoppel={{
                          ...service?.orders?.find(v => v.orderType === 'Estoppel'),
                          assignedProcessors:
                            processors.filter(p =>
                              service?.orders
                                ?.find(v => v.orderType === 'Estoppel')
                                ?.assignedProcessors?.includes(p._id)
                            ) || []
                        }}
                        onEditClick={onOrderEditClick}
                        onEditHoaSlotClick={onEditHoaSlotClick}
                        onDeleteClick={onOrderDeleteClick}
                      />
                    )}
                    {a.key === 'survey' && service?.orders?.find(v => v.orderType === 'Survey') && (
                      <Survey
                        serviceCreation={true}
                        survey={{
                          ...service?.orders?.find(v => v.orderType === 'Survey'),
                          assignedProcessors:
                            processors.filter(p =>
                              service?.orders?.find(v => v.orderType === 'Survey')?.assignedProcessors?.includes(p._id)
                            ) || []
                        }}
                        onEditClick={onOrderEditClick}
                        onDeleteClick={onOrderDeleteClick}
                      />
                    )}
                  </Card>
                </div>
              )}
            </React.Fragment>
          ))}

          <AddEditOrderSidebar
            isPublic={isPublic}
            orderType={addUpdateOrderMeta?.title || ''}
            show={addUpdateOrderMeta !== null}
            onHide={() => setAddUpdateOrderMeta(null)}
            onOrderAddUpdate={onUpdateFormOrder}
            serviceCreation={true}
            draftOnly={
              addUpdateOrderMeta?.title === 'Estoppel' && !!addUpdateOrderMeta?.object?._id
            }
            hoaEditIndex={addUpdateOrderMeta?.hoaEditIndex ?? null}
            editingOrder={addUpdateOrderMeta?.object}
            service={{ ...service, organization: selectedOrganization }}
          />
        </>
      )}
    </SlidingSideBar>
  );
};

export default AddEditServiceSidebar;
